import type { Condo, Condominium } from '~/core/entities/condo/Condo';
import type { PaginatedCondoFilters } from '~/core/entities/condo/Filters';
import type { ResultTuple } from '~~/core/helpers/result';
import type { NuxtListPropertiesApiReturn } from '~~/server/api/condos/index.get';

import { Result } from '~~/core/helpers/result';
import { createOriginalErrorObject } from '~~/server/utils/errorHandling';

interface NuxtCondosRepository {
	getCondoByIdentifier: (
		identifier: Condo['id']
	) => Promise<ResultTuple<Condo>>;
	getCondominiumBySlug: (
		slug: Condo['slug']
	) => Promise<ResultTuple<Condominium>>;
	list: (
		filters: Partial<PaginatedCondoFilters>
	) => Promise<ResultTuple<NuxtListPropertiesApiReturn>>;
}

export const nuxtCondosRepository: NuxtCondosRepository = {
	async getCondoByIdentifier(identifier) {
		if (!identifier) {
			return Result.error({
				message: 'Identifier is required.',
				source: 'nuxtCondosRepository.getOneByIdentifier',
				originalErrorObject: new Error('Identifier is required.')
			});
		}

		try {
			const response = await $fetch<Condo>(`/api/condos/${identifier}`);

			return Result.ok(response);
		} catch (error: any) {
			if (error.statusCode === 404) {
				return Result.error({
					message: `Condominium by identifier ${identifier} not found`,
					source: 'nuxtCondosRepository.getOneByIdentifier',
					originalErrorObject: createOriginalErrorObject(error)
				});
			}

			return Result.error({
				message: `Error while fetching the condominium by identifier ${identifier}.`,
				source: 'nuxtCondosRepository.getOneByIdentifier',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async getCondominiumBySlug(slug) {
		if (!slug) {
			return Result.error({
				message: 'Slug is required.',
				source: 'nuxtCondosRepository.getCondominiumBySlug',
				originalErrorObject: new Error('Slug is required.')
			});
		}

		try {
			const response = await $fetch<Condominium>(`/api/condominium/${slug}`);

			return Result.ok(response);
		} catch (error: any) {
			if (error.statusCode === 404) {
				return Result.error({
					message: `Condominium by slug ${slug} not found`,
					source: 'nuxtCondosRepository.getCondominiumBySlug',
					originalErrorObject: createOriginalErrorObject(error)
				});
			}

			return Result.error({
				message: `Error while fetching the condominium by slug ${slug}.`,
				source: 'nuxtCondosRepository.getCondominiumBySlug',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async list(filters) {
		try {
			const response = await $fetch<NuxtListPropertiesApiReturn>(
				'/api/condos',
				{
					method: 'GET',
					query: filters
				}
			);

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while fetching condos from the API.',
				source: 'nuxtCondosRepository.list',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	}
};
